import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import { addCommas } from '../../utils/functions/commas';
import { getStorage } from '../../utils/functions/localstorage';
import { useGetAttachmentMutation } from '../../services/eDocuments';
import NotiAlert from '../NotiAlert/NotiAlert';
import CircularProgress from '@mui/material/CircularProgress';
import { TableSortLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/material/';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import SubInvoiceTable from './SubInvoiceTable';

export const E_INVOICE = 'E_INVOICE';
const E_RECEIPT = 'E_RECEIPT';
const ATTACHMENT = 'ATTACHMENT';
const E_CREDIT_NOTE = 'E_CREDIT_NOTE';
const PAID = 'Paid';
const OVERDUE = 'Overdue';
const UNPAID = 'Unpaid';
const PENDING = 'Pending';
const CREDIT_NOTE = 'Credit Note';
const PARTIAL = 'Partial';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fff',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    position: 'relative',
    zIndex: 10,
    cursor: 'pointer',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: '#d0f8ce',
  },
  transition: 'transform 0.15s ',
}));

export default function TableInvoice({
  invoiceItems,
  selectedRows,
  setSelectedRows,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  total,
  isFetching,
  sortOrder,
  setSortOrder,
  custNO,
  customerName,
}) {
  const { t } = useTranslation();
  const [openRows, setOpenRows] = useState(new Set());
  const [openDialog, setOpenDialog] = useState(false);
  const [servicename, setServiceName] = useState('');
  const getDocumentCustomerCode = (doc = {}) =>
    doc.customer_code ||
    doc.customer?.customer_code ||
    doc.invoice_period?.sold_to_party ||
    custNO;

  const handleToggleRow = (so_number) => {
    setOpenRows((prev) => {
      const newOpenRows = new Set(prev);
      if (newOpenRows.has(so_number)) {
        newOpenRows.delete(so_number);
      } else {
        newOpenRows.add(so_number);
      }
      return newOpenRows;
    });
  };

  const mainColumns = [
    {
      id: 'so_number',
      label: t('app.saleOrder'),
      minWidth: 150,
      align: 'center',
    },
    {
      id: 'quotation',
      label: t('app.quotation'),
      minWidth: 150,
      align: 'center',
    },
    {
      id: 'service_name',
      label: t('app.serviceName'),
      align: 'center',
      minWidth: 200,
    },
    {
      id: 'customer_name',
      label: t('navbar.custName'),
      minWidth: 150,
      align: 'center',
    },
    {
      id: 'contract_start_date',
      label: t('app.startDate'),
      minWidth: 150,
      align: 'center',
    },
    {
      id: 'contract_end_date',
      label: t('app.endDate'),
      minWidth: 150,
      align: 'center',
    },
    {
      id: 'grand_amount',
      label: t('app.grandAmount'),
      minWidth: 150,
      align: 'center',
    },
    {
      id: 'period_total',
      label: t('app.period'),
      minWidth: 150,
      align: 'center',
    },
  ];

  const subColumns = [
    {
      id: 'invoice_no',
      label: t('app.invoiceNo'),
      align: 'center',
      minWidth: 140,
    },
    {
      id: 'payment_status',
      label: t('app.statusPayment'),
      align: 'center',
      minWidth: 100,
    },
    {
      id: 'customer_name',
      label: t('navbar.custName'),
      align: 'center',
      minWidth: 200,
    },
    {
      id: 'invoice_grand_total',
      label: t('app.total'),
      align: 'center',
      minWidth: 100,
    },
    {
      id: 'period_label',
      label: t('app.period'),
      align: 'center',
      minWidth: 80,
    },
    {
      id: 'invoice_date',
      label: t('app.invoiceDate'),
      align: 'center',
      minWidth: 80,
    },
    {
      id: 'period_start_date',
      label: t('app.startDate'),
      align: 'center',
      minWidth: 120,
    },
    {
      id: 'period_end_date',
      label: t('app.endDate'),
      align: 'center',
      minWidth: 120,
    },
    { id: 'due_date', label: t('app.dueDate'), align: 'center', minWidth: 120 },
    {
      id: 'credit_term',
      label: t('app.creditTerm'),
      align: 'center',
      minWidth: 100,
    },
    {
      id: 'receipt_document',
      label: t('app.receiptDocument'),
      align: 'center',
      minWidth: 100,
    },
    {
      id: 'credit_note',
      label: t('app.creditNote'),
      align: 'center',
      minWidth: 100,
    },
    {
      id: 'payment_type',
      label: t('app.paymentType'),
      align: 'center',
      minWidth: 120,
    },
    {
      id: 'payment_date',
      label: t('app.paymentDate'),
      align: 'center',
      minWidth: 120,
    },
    {
      id: 'dowloadFile',
      label: t('app.eDocuments'),
      align: 'center',
      minWidth: 80,
    },
  ];

  const accesstoken = getStorage('accesstoken');
  const [getAttachment] = useGetAttachmentMutation();
  const [openAlert, setOpenAlert] = useState(false);
  const [titleAlert, setTitleAlert] = useState('');
  const [valueAlert, setValueAlert] = useState('');
  const token = localStorage.getItem('token');
  const roleAcc = token ? jwtDecode(token).ROLE : '';
  const [downloading, setDownloading] = useState(false);

  const handleSort = (field) => {
    setSortOrder((prev) => {
      if (!prev || prev.field !== field) {
        return { field, direction: 'asc' };
      }
      return {
        field,
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  const alertMethods = {
    handleOnClose: () => {
      setOpenAlert(false);
    },
    main: (open, title, value) => {
      setOpenAlert(open);
      setTitleAlert(title);
      setValueAlert(value);
    },
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const PDF_MAGIC = 'JVBERi0xLj';

  const sanitizeBase64 = (s) => {
    if (!s || typeof s !== 'string') return '';
    let t = s.trim();
    const i = t.indexOf('base64,');
    if (i !== -1) t = t.slice(i + 'base64,'.length);
    t = t.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
    const mod = t.length % 4;
    if (mod === 2) t += '==';
    else if (mod === 3) t += '=';
    else if (mod === 1) return '';
    return t;
  };

  const firstValidBase64 = (input) => {
    if (Array.isArray(input)) {
      for (const x of input) {
        const b = sanitizeBase64(x);
        if (b) return b;
      }
      return '';
    }
    return sanitizeBase64(input);
  };

  const guessMime = (b64, explicitMime) =>
    explicitMime ||
    (b64.startsWith(PDF_MAGIC) ? 'application/pdf' : 'image/png');

  const toDataUrl = (b64, explicitMime) =>
    `data:${guessMime(b64, explicitMime)};base64,${b64}`;

  const triggerDownload = (filename, dataUrl) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const [documentSelectList, setDocumentSelectList] = useState(null);

  const downloadDoc = {
    eReceipt: async (doc) => {
      setDownloading(true);

      const domain =
        roleAcc === 'ADMIN' ||
        roleAcc === 'SUPER ADMIN' ||
        roleAcc === 'SALE' ||
        roleAcc === 'SALES'
          ? process.env.REACT_APP_API_URL_admin
          : process.env.REACT_APP_API_URL;

      try {
        const apiUrl = `${domain}/reports/e-receipt/?invoiceNo=${doc.invoice_no}&sorgcode=${doc.sorgcode}&customerCode=${getDocumentCustomerCode(doc)}&customerName=${customerName}`;

        const { data } = await axios.get(apiUrl);
        const payload = data?.data ?? data ?? {};
        const raw = firstValidBase64(
          payload.file ?? payload.File ?? data?.pdfUrl ?? '',
        );

        if (!raw) {
          throw new Error('ไม่พบไฟล์จากเส้นทางหลัก');
        }

        triggerDownload(
          `e-receipt-${doc.invoice_no}.pdf`,
          toDataUrl(raw, 'application/pdf'),
        );
      } catch (error) {
        // --- Fallback E-Receipt ---
        try {
          console.log(
            'เส้นหลักไม่มีข้อมูล กำลังดึงจากเส้นทางสำรอง e-receipt...',
          );

          const fallbackUrl = `${domain}/payment-be/get-ereceipt`;

          const fallbackResponse = await axios.post(fallbackUrl, {
            invoiceNo: doc.invoice_no,
          });

          const fallbackData = fallbackResponse.data;
          const fallbackPayload = fallbackData?.data ?? fallbackData ?? {};

          if (fallbackPayload.pdfURL && fallbackPayload.pdfURL.length > 0) {
            const pdfLink = fallbackPayload.pdfURL[0];

            if (pdfLink.startsWith('http')) {
              const a = document.createElement('a');
              a.href = pdfLink;
              a.target = '_blank';
              a.download = `e-receipt-${doc.invoice_no}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              return;
            }
          }

          const fallbackRaw = firstValidBase64(
            fallbackPayload.file ??
              fallbackPayload.File ??
              fallbackPayload.base64 ??
              '',
          );

          if (!fallbackRaw) {
            throw new Error('ไม่มีข้อมูล e-receipt จากทั้งสองเส้นทาง');
          }

          triggerDownload(
            `e-receipt-${doc.invoice_no}.pdf`,
            toDataUrl(fallbackRaw, 'application/pdf'),
          );
        } catch (fallbackError) {
          const errorMsg =
            fallbackError?.response?.data?.error?.message ||
            fallbackError?.response?.data?.message ||
            fallbackError?.message ||
            'เกิดข้อผิดพลาดในการดาวน์โหลดเอกสาร';

          alertMethods.main(true, errorMsg, 'error');
        }
      } finally {
        setDownloading(false);
      }
    },

    eInvoice: async (doc) => {
      setDownloading(true);

      const domain =
        roleAcc === 'ADMIN' ||
        roleAcc === 'SUPER ADMIN' ||
        roleAcc === 'SALE' ||
        roleAcc === 'SALES'
          ? process.env.REACT_APP_API_URL_admin
          : process.env.REACT_APP_API_URL;

      try {
        const apiUrl = `${domain}/reports/e-invoice/?invoiceNo=${doc.invoice_no}&sorgcode=${doc.sorgcode}&customerCode=${getDocumentCustomerCode(doc)}&customerName=${customerName}`;

        const { data } = await axios.get(apiUrl);

        if (data?.data?.status === 'Not Found')
          throw new Error('ไม่มีข้อมูล e-invoice จากเส้นทางหลัก');

        const payload = data?.data ?? data ?? {};

        // เช็คว่า API เส้นหลักส่งลิงก์ URL มาให้หรือไม่
        let pdfLink = '';
        if (Array.isArray(payload.pdfURL) && payload.pdfURL.length > 0)
          pdfLink = payload.pdfURL[0];
        else if (typeof payload.pdfUrl === 'string') pdfLink = payload.pdfUrl;

        if (pdfLink && pdfLink.startsWith('http')) {
          const a = document.createElement('a');
          a.href = pdfLink;
          a.target = '_blank';
          a.download = `e-invoice-${doc.invoice_no}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }

        const raw = firstValidBase64(
          payload.file ?? payload.File ?? payload.files ?? '',
        );

        if (!raw) throw new Error('ไม่พบไฟล์ e-invoice จากเส้นทางหลัก');

        triggerDownload(
          `e-invoice-${doc.invoice_no}.pdf`,
          toDataUrl(raw, 'application/pdf'),
        );
      } catch (error) {
        // --- Fallback E-Invoice ---
        try {
          console.log(
            'เส้นหลักไม่มีข้อมูล กำลังดึงจากเส้นทางสำรอง e-invoice...',
          );

          // ✅ เปลี่ยน Endpoint เป็น get-einvoice
          const fallbackUrl = `${domain}/payment-be/get-einvoice`;

          const fallbackResponse = await axios.post(fallbackUrl, {
            invoiceNo: doc.invoice_no,
          });

          const fallbackData = fallbackResponse.data;
          const fallbackPayload = fallbackData?.data ?? fallbackData ?? {};

          // เช็คกรณีส่งลิงก์กลับมา
          if (fallbackPayload.pdfURL && fallbackPayload.pdfURL.length > 0) {
            const pdfLink = fallbackPayload.pdfURL[0];

            if (pdfLink.startsWith('http')) {
              const a = document.createElement('a');
              a.href = pdfLink;
              a.target = '_blank';
              a.download = `e-invoice-${doc.invoice_no}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              return;
            }
          }

          // เช็คกรณีส่ง Base64 กลับมา
          const fallbackRaw = firstValidBase64(
            fallbackPayload.file ??
              fallbackPayload.File ??
              fallbackPayload.base64 ??
              '',
          );

          if (!fallbackRaw) {
            throw new Error('ไม่มีข้อมูล e-invoice จากทั้งสองเส้นทาง');
          }

          triggerDownload(
            `e-invoice-${doc.invoice_no}.pdf`,
            toDataUrl(fallbackRaw, 'application/pdf'),
          );
        } catch (fallbackError) {
          const errorMsg =
            fallbackError?.response?.data?.error?.message ||
            fallbackError?.response?.data?.message ||
            fallbackError?.message ||
            'เกิดข้อผิดพลาดในการดาวน์โหลดเอกสารใบแจ้งหนี้';

          alertMethods.main(true, errorMsg, 'error');
        }
      } finally {
        setDownloading(false);
      }
    },

    attachment: async (doc) => {
      setDownloading(true);
      try {
        const soNumber = doc.so_number || doc.invoice_period?.so_number;
        if (!soNumber) throw new Error('ไม่พบ SO Number');

        const data = await getAttachment({
          so_number: soNumber,
          accesstoken,
        }).unwrap();

        const raw = firstValidBase64(data?.result);
        if (!raw) throw new Error(`ไม่พบข้อมูลไฟล์แนบของ ${soNumber}`);

        const mime = guessMime(raw);
        triggerDownload(
          `attachment-${soNumber}.${
            mime === 'application/pdf' ? 'pdf' : 'png'
          }`,
          `data:${mime};base64,${raw}`,
        );
      } catch (error) {
        alertMethods.main(
          true,
          error?.response?.data?.error?.message ||
            error?.message ||
            'เกิดข้อผิดพลาด',
          'error',
        );
      } finally {
        setDownloading(false);
      }
    },

    eCredit: async (doc) => {
      setDownloading(true);
      try {
        const domain =
          roleAcc === 'ADMIN' ||
          roleAcc === 'SUPER ADMIN' ||
          roleAcc === 'SALE' ||
          roleAcc === 'SALES'
            ? process.env.REACT_APP_API_URL_admin
            : process.env.REACT_APP_API_URL;
            
        const apiUrl = `${domain}/reports/e-credit-note/?invoiceNo=${doc.invoice_no}&sorgcode=${doc.sorgcode}&customerCode=${getDocumentCustomerCode(doc)}&customerName=${customerName}`;

        const { data } = await axios.get(apiUrl);

        if (data?.data?.status === 'Not Found')
          throw new Error('ไม่มีข้อมูล E-Credit Note');

        const payload = data?.data ?? data ?? {};

        let pdfLink = '';
        if (Array.isArray(payload.pdfURL) && payload.pdfURL.length > 0)
          pdfLink = payload.pdfURL[0];
        else if (typeof payload.pdfUrl === 'string') pdfLink = payload.pdfUrl;

        if (pdfLink && pdfLink.startsWith('http')) {
          const a = document.createElement('a');
          a.href = pdfLink;
          a.target = '_blank';
          a.download = `e-credit-note-${doc.invoice_no}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }

        const raw = firstValidBase64(
          payload.file ?? payload.File ?? payload.files ?? payload.pdfUrl ?? '',
        );

        if (!raw)
          throw new Error(`ไม่พบไฟล์ E-Credit Note ของ ${doc.invoice_no}`);

        triggerDownload(
          `e-credit-note-${doc.invoice_no}.pdf`,
          toDataUrl(raw, 'application/pdf'),
        );
      } catch (error) {
        alertMethods.main(
          true,
          error?.response?.data?.error?.message ||
            error?.message ||
            'เกิดข้อผิดพลาด',
          'error',
        );
      } finally {
        setDownloading(false);
      }
    },

    main: (documentType) => {
      if (documentType === E_RECEIPT) downloadDoc.eReceipt(documentSelectList);
      if (documentType === E_INVOICE) downloadDoc.eInvoice(documentSelectList);
      if (documentType === ATTACHMENT)
        downloadDoc.attachment(documentSelectList);
      if (documentType === E_CREDIT_NOTE)
        downloadDoc.eCredit(documentSelectList);
      setDocumentSelectList(null);
      handleCloseDialog();
    },
  };

  const handleBothClicks = (event, item) => {
    setDocumentSelectList(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => setDocumentSelectList(null), 200);
  };

  const getSortLabelSx = (activeField, currentField) => ({
    color: activeField === currentField ? '#222' : '#fff',
    '& .MuiTableSortLabel-icon': {
      color:
        activeField === currentField ? '#222 !important' : '#fff !important',
      opacity: 1,
    },
  });

  const renderCellContent = (columnId, item, isSubTable = false) => {
    let newValue;
    if (columnId === 'so_number' && item.so_number) {
      newValue = item.so_number;
    } else if (columnId === 'customer_name' && item.customer) {
      newValue = item.customer.customer_name;
    } else if (columnId === 'service_name') {
      newValue =
        item.service_name || item.invoice?.[0]?.service_name || item.invoice?.[0]?.invoice_period?.material_name;
    } else if (columnId === 'receipt_document') {
      newValue = item.invoice_period?.receipt_document || item.receipt_document;
    } else if (columnId === 'credit_note') {
      newValue = item.credit_note;
    } else {
      newValue = item[columnId];
    }

    if (columnId === 'grand_amount' || columnId === 'invoice_grand_total') {
      return <Typography variant="body2">{addCommas(newValue, 2)}</Typography>;
    }

    if (
      (columnId.includes('_date') || columnId.includes('date')) &&
      newValue &&
      typeof newValue === 'string'
    ) {
      const dateString =
        newValue.length === 8
          ? `${newValue.substring(0, 4)}-${newValue.substring(
              4,
              6,
            )}-${newValue.substring(6, 8)}`
          : newValue;
      return (
        <Typography
          variant="body2"
          align="center"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {dayjs(dateString).isValid()
            ? dayjs(dateString).format('DD MMM YYYY')
            : '-'}
        </Typography>
      );
    }

    if (columnId === 'payment_status') {
      const statusConfig = {
        [PAID]: {
          label: t('app.paid') || 'Paid',
          sx: { borderColor: '#4caf50', color: '#2e7d32' },
        },
        [OVERDUE]: {
          label: t('app.overdue') || 'Overdue',
          sx: { borderColor: '#ff1744', color: '#ff1744' },
        },
        [PENDING]: {
          label: t('app.pending') || 'Pending',
          sx: { borderColor: '#f9a825', color: '#f57f17' },
        },
        [UNPAID]: {
          label: t('app.unpaid') || 'Unpaid',
          sx: { borderColor: '#ff1744', color: '#ff1744' },
        },
        [CREDIT_NOTE]: {
          label: 'Credit Note',
          sx: {
            backgroundColor: '#d0d0d0',
            borderColor: '#bdbdbd',
            color: '#111',
          },
        },
        [PARTIAL]: {
          label: 'Partial',
          sx: {
            borderColor: '#e0b800',
            color: '#e0b800',
          },
        },
      };
      const config = statusConfig[newValue] || {
        label: newValue,
        sx: { borderColor: '#9e9e9e', color: '#616161' },
      };
      return (
        <Chip
          label={config.label}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 600,
            minWidth: '80px',
            whiteSpace: 'nowrap',
            ...config.sx,
          }}
        />
      );
    }

    if (columnId === 'payment_type') {
      let cellText;
      switch (newValue) {
        case 'Post-Paid':
          cellText = t('app.postPaid') || 'Post-Paid';
          break;
        case 'Pre-Paid':
          cellText = t('app.prePaid') || 'Pre-Paid';
          break;
        default:
          cellText = newValue;
      }
      return (
        <Typography
          variant="body2"
          sx={{
            color: newValue === 'Post-Paid' ? '#0288D1' : '#01579B',
            whiteSpace: 'nowrap',
          }}
        >
          {cellText}
        </Typography>
      );
    }

    if (columnId === 'so_number') {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',

            color: isSubTable ? '' : 'primary.main',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
          }}
        >
          {newValue || '-'}
        </Typography>
      );
    }
    if (columnId === 'quotation') {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontFamily: 'monospace',
          }}
        >
          {newValue || '-'}
        </Typography>
      );
    }
    if (columnId === 'invoice_no') {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontFamily: 'monospace',
          }}
        >
          {newValue || '-'}
        </Typography>
      );
    }

    if (columnId === 'dowloadFile') {
      return (
        <IconButton
          id="basic-menu"
          onClick={(event) => handleBothClicks(event, item)}
          size="small"
        >
          <DescriptionOutlinedIcon
            fontSize="small"
            sx={{ color: 'primary.light' }}
          />
        </IconButton>
      );
    }

    return newValue || '-';
  };

  return (
   <div className="w-full overflow-hidden border border-slate-200">
      <NotiAlert
        open={openAlert}
        setOpen={setOpenAlert}
        title={titleAlert}
        value={valueAlert}
        onButtonClick={alertMethods.handleOnClose}
      />
      {downloading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={80} />
        </div>
      )}
      <Paper>
       <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>
          {' '}
          <Table size="big">
            <TableHead>
              <TableRow>
                {mainColumns.map((column) => {
                  const isSortable =
                    column.id === 'quotation' || column.id === 'grand_amount';

                  return (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{ padding: '10px', fontWeight: 'bold' }}
                    >
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {column.label}
                        {isSortable && (
                          <TableSortLabel
                            active={sortOrder?.field === column.id}
                            direction={
                              sortOrder?.field === column.id
                                ? sortOrder.direction
                                : 'asc'
                            }
                            onClick={() => handleSort(column.id)}
                            sx={getSortLabelSx(sortOrder?.field, column.id)}
                          />
                        )}
                      </div>
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={mainColumns.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : _.size(invoiceItems) ? (
                _.map(invoiceItems, (item, index) => {
                  const isRowOpen = openRows.has(item.so_number);
                  const hasInvoice = item.invoice && item.invoice.length > 0;

                  return (
                    <React.Fragment key={index}>
                      <StyledTableRow
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          hasInvoice && handleToggleRow(item.so_number)
                        }
                        sx={{
                          '& > *': { borderBottom: 'unset' },
                          cursor: hasInvoice ? 'pointer' : 'default',
                          transition: 'all 0.2s',
                          borderLeft: isRowOpen
                            ? '6px solid #4caf50'
                            : '6px solid transparent',
                        }}
                      >
                        {mainColumns.map((column) => (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === 'so_number' ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                              >
                                <IconButton
                                  size="small"
                                  disabled={!hasInvoice}
                                  sx={{
                                    p: 0.5,
                                  }}
                                >
                                  {isRowOpen ? (
                                    <KeyboardArrowUpIcon fontSize="small" />
                                  ) : (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  )}
                                </IconButton>
                                {renderCellContent(column.id, item)}
                              </Stack>
                            ) : (
                              renderCellContent(column.id, item)
                            )}
                          </TableCell>
                        ))}
                      </StyledTableRow>

                      <SubInvoiceTable
                        soItem={item}
                        isRowOpen={isRowOpen}
                        mainColumns={mainColumns}
                        subColumns={subColumns}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        renderCellContent={renderCellContent}
                      />
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={mainColumns.length} align="center">
                    <div>{t('app.noDataAvailable')}</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${addCommas(count, 0)}`
          }
        />

        <Dialog
          onClose={handleCloseDialog}
          open={openDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
            {t('app.selectDocument')}
            <Typography color="primary" sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {documentSelectList?.invoice_no || '-'}
            </Typography>
          </DialogTitle>

          <List sx={{ pt: 0 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => downloadDoc.main(E_INVOICE)}>
                <ListItemIcon>
                  <DescriptionOutlinedIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={t('app.eInvoice')} />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={() => downloadDoc.main(E_RECEIPT)}>
                <ListItemIcon>
                  <ReceiptLongOutlinedIcon color="success" />
                </ListItemIcon>
                <ListItemText primary={t('app.eReceipt')} />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={() => downloadDoc.main(E_CREDIT_NOTE)}>
                <ListItemIcon>
                  <EditNoteOutlinedIcon color="warning" />
                </ListItemIcon>
                <ListItemText primary="E-Credit Note" />
              </ListItemButton>
            </ListItem>
          </List>
        </Dialog>
      </Paper>
    </div>
  );
}
